import React, { useEffect, useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { usePortalReports, computeProgress } from './PortalReportsContext';

const useNow = (active) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
};

const fmtRemaining = (ms) => {
  if (ms == null) return 'calculando tempo restante…';
  if (ms <= 0) return 'finalizando…';
  const s = Math.ceil(ms / 1000);
  return s < 60 ? `cerca de ${s}s restantes` : `cerca de ${Math.ceil(s / 60)} min restantes`;
};

export const PortalReportsStatus = ({ entryKey }) => {
  const ctx = usePortalReports();
  const entry = ctx?.entries[entryKey];
  const loading = !!entry?.loading;
  const now = useNow(loading);
  const [showDone, setShowDone] = useState(false);

  useEffect(() => {
    if (!entry?.finishedAt || Date.now() - entry.finishedAt > 3000) return undefined;
    setShowDone(true);
    const id = setTimeout(() => setShowDone(false), 3000 - (Date.now() - entry.finishedAt));
    return () => clearTimeout(id);
  }, [entry?.finishedAt]);

  if (!entry || (!loading && !showDone)) return null;
  if (!loading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800" data-testid="portal-reports-status-done">
        <CheckCircle2 className="h-4 w-4" /> Relatório carregado
      </div>
    );
  }
  const p = computeProgress(entry, ctx.estimates, now);
  return (
    <div className="rounded-lg border bg-white px-4 py-3 space-y-2" data-testid="portal-reports-status">
      <p className="text-sm flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-indigo-600 shrink-0" />
        <span data-testid="portal-reports-status-text">
          Carregando relatório: {p.done} de {p.total} seções ({p.pct}%) · {fmtRemaining(p.remainingMs)}
        </span>
      </p>
      <Progress value={p.pct} className="h-1.5" />
    </div>
  );
};

// Small indicator for the "Métricas" menu item while loading elsewhere / ready unseen.
export const MetricsMenuIndicator = ({ active }) => {
  const ctx = usePortalReports();
  const entry = ctx?.currentKey ? ctx.entries[ctx.currentKey] : null;
  if (!ctx || active) return null;
  if (entry?.loading) {
    const p = computeProgress(entry, ctx.estimates, Date.now());
    return (
      <span className="shrink-0 text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-indigo-500/20 text-indigo-200" data-testid="portal-metricas-progress">
        {p.pct}%
      </span>
    );
  }
  if (ctx.readyUnseen) {
    return <span className="shrink-0 h-2 w-2 rounded-full bg-emerald-400" title="Relatório pronto" data-testid="portal-metricas-ready" />;
  }
  return null;
};
